import type { SvgIconProps } from '@mui/material/SvgIcon';
import type { Theme, Components } from '@mui/material/styles';

import SvgIcon from '@mui/material/SvgIcon';
import { selectClasses } from '@mui/material/Select';
import { nativeSelectClasses } from '@mui/material/NativeSelect';

import { varAlpha } from '../../styles';

// ----------------------------------------------------------------------

/**
 * Icons
 * https://icon-sets.iconify.design/eva/arrow-ios-downward-fill/
 */
const ArrowDownIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path
      fill="currentColor"
      d="M12 16a1 1 0 0 1-.64-.23l-6-5a1 1 0 1 1 1.28-1.54L12 13.71l5.36-4.32a1 1 0 0 1 1.41.15a1 1 0 0 1-.14 1.46l-6 4.83A1 1 0 0 1 12 16"
    />
  </SvgIcon>
);

// ----------------------------------------------------------------------

const selectPaper = {
  sx: (theme: Theme) => ({
    mt: 0.75,
    p: 0.5,
    border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
    boxShadow: theme.customShadows.dropdown,
    borderRadius: `${theme.shape.borderRadius * 1.25}px`,
    backgroundImage: 'none',
  }),
};

const MuiSelect: Components<Theme>['MuiSelect'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: {
    IconComponent: ArrowDownIcon,
    MenuProps: { PaperProps: selectPaper },
  },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    select: ({ theme }) => ({
      minHeight: 'unset',
      color: theme.vars.palette.text.primary,
      '&:focus': {
        borderRadius: 'inherit',
        backgroundColor: 'transparent',
      },
      [`&.${selectClasses.filled}`]: {
        paddingRight: theme.spacing(4.5),
      },
      [`&.${selectClasses.outlined}`]: {
        paddingRight: theme.spacing(4.5),
      },
    }),
    icon: ({ theme }) => ({
      right: 12,
      width: 18,
      height: 18,
      top: 'calc(50% - 9px)',
      color: theme.vars.palette.text.disabled,
      transition: theme.transitions.create(['color', 'transform'], {
        duration: theme.transitions.duration.shortest,
      }),
      [`&.${selectClasses.iconOpen}`]: {
        color: theme.vars.palette.text.primary,
        transform: 'rotate(180deg)',
      },
      [`&.${selectClasses.disabled}`]: {
        color: theme.vars.palette.action.disabled,
      },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiNativeSelect: Components<Theme>['MuiNativeSelect'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { IconComponent: ArrowDownIcon },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    select: ({ theme }) => ({
      minHeight: 'unset',
      color: theme.vars.palette.text.primary,
      '&:focus': {
        borderRadius: 'inherit',
        backgroundColor: 'transparent',
      },
      [`&.${nativeSelectClasses.filled}`]: {
        paddingRight: theme.spacing(4.5),
      },
      [`&.${nativeSelectClasses.outlined}`]: {
        paddingRight: theme.spacing(4.5),
      },
    }),
    icon: ({ theme }) => ({
      right: 12,
      width: 18,
      height: 18,
      top: 'calc(50% - 9px)',
      color: theme.vars.palette.text.disabled,
      transition: theme.transitions.create(['color', 'transform'], {
        duration: theme.transitions.duration.shortest,
      }),
      [`&.${nativeSelectClasses.disabled}`]: {
        color: theme.vars.palette.action.disabled,
      },
    }),
  },
};

// ----------------------------------------------------------------------

export const select = { MuiSelect, MuiNativeSelect };
